import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaFileInvoiceDollar, FaRegEdit } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import { FiDownload, FiUploadCloud, FiMail } from "react-icons/fi";
import { useI18n } from "../../i18n/I18nContext";
import DateRangePicker from "../common/DateRangePicker";
import MultiSelect from "../common/MultiSelect";
import AutocompleteSelect from "../common/AutocompleteSelect";
import Spinner from "../spinner/Spinner";
import EmptyState from "../common/EmptyState";
import NeedOrganizationEmptyState from "../common/NeedOrganizationEmptyState";
import Button from "../common/Button";
import Tooltip from "../common/Tooltip";
import ConfirmModal from "../common/ConfirmModal";
import GenerateInvoiceIdModal from "./GenerateInvoiceIdModal";
import toastOptions from "../../Redux/toastOptions";
import { listClientsApi } from "../../Redux/Clients/Client.actions";
import { listProjectsApi } from "../../Redux/Projects/Project.actions";
import { listMembersApi } from "../../Redux/Organizations/Org.actions";
import { fetchLatestEntryDayApi } from "../../Redux/Tasks/Task.actions";
import {
  listInvoicesApi,
  previewInvoiceApi,
  generateInvoiceApi,
  downloadInvoicePdfApi,
  updateInvoiceStatusApi,
  reuploadInvoiceApi,
  sendInvoiceEmailApi,
  deleteInvoiceApi,
  clearInvoices,
} from "../../Redux/Invoices/Invoice.actions";
import { dateRangeShortcuts, toISODate, fromISODate } from "../common/dateRangeShortcuts";
import { isAdminOrOwner as computeIsAdminOrOwner } from "../common/permissions";
import { apiErrorMessage } from "../../i18n/translate";
import styles from "./Styles/Invoices.module.css";

// Invoices are generated a month at a time in practice, so the picker only
// offers those two shortcuts rather than the full report shortcut list.
const INVOICE_SHORTCUT_KEYS = ["thisMonth", "lastMonth"];

const STATUSES = ["unpaid", "paid", "canceled", "refunded"];
const STATUS_LABEL_KEY = {
  unpaid: "invoices.statusUnpaid",
  paid: "invoices.statusPaid",
  canceled: "invoices.statusCanceled",
  refunded: "invoices.statusRefunded",
};
const STATUS_BADGE_CLASS = {
  unpaid: "statusUnpaid",
  paid: "statusPaid",
  canceled: "statusCanceled",
  refunded: "statusRefunded",
};

// referenceDate defaults to today - pass the user's most recently logged
// time entry's date instead once it's known (see the effect below), so the
// initial range's end reflects their last activity rather than today when
// they haven't logged anything recently. Start is always the first day of
// that reference date's month.
const defaultRange = (t, referenceDate) => {
  const thisMonth = dateRangeShortcuts(t, referenceDate).find((s) => s.key === "thisMonth");
  const [s, e] = thisMonth.range();
  return { start: toISODate(s), end: toISODate(e) };
};

const InvoiceRow = ({ invoice, orgId, token, onDelete }) => {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(invoice.status);

  const handleSave = async () => {
    await dispatch(updateInvoiceStatusApi(orgId, invoice.id, status, token));
    setEditing(false);
  };

  return (
    <li className={`cw-list-item ${styles.invoiceRow}`}>
      <span className={styles.number}>{invoice.number}</span>
      <span className={styles.period}>
        {invoice.selectedBeginDate} - {invoice.selectedEndDate}
      </span>
      {editing ? (
        <select className="cw-select" value={status} onChange={(e) => setStatus(e.target.value)} title={t("invoices.status")}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {t(STATUS_LABEL_KEY[s])}
            </option>
          ))}
        </select>
      ) : (
        <span className={`${styles.statusBadge} ${styles[STATUS_BADGE_CLASS[invoice.status]] || ""}`}>
          {t(STATUS_LABEL_KEY[invoice.status])}
        </span>
      )}
      <span className={styles.total}>{invoice.totalTTC.toFixed(2)}</span>
      <div className={styles.rowActions}>
        <Tooltip label={t("invoices.download")}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => dispatch(downloadInvoicePdfApi(orgId, invoice.id, token))}
          >
            <FiDownload style={{ fontSize: "16px" }} />
          </button>
        </Tooltip>
        <Tooltip label={t("invoices.reupload")}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => dispatch(reuploadInvoiceApi(orgId, invoice.id, token))}
          >
            <FiUploadCloud style={{ fontSize: "16px" }} />
          </button>
        </Tooltip>
        <Tooltip label={t("invoices.sendInvoice")}>
          <button
            type="button"
            className={styles.iconBtn}
            onClick={() => dispatch(sendInvoiceEmailApi(orgId, invoice.id, token))}
          >
            <FiMail style={{ fontSize: "16px" }} />
          </button>
        </Tooltip>
        {editing ? (
          <Button size="sm" onClick={handleSave} title={t("invoices.saveStatus")}>
            {t("common.save")}
          </Button>
        ) : (
          <Tooltip label={t("invoices.editStatus")}>
            <button type="button" className={styles.iconBtn} onClick={() => setEditing(true)}>
              <FaRegEdit style={{ fontSize: "16px" }} />
            </button>
          </Tooltip>
        )}
        <Tooltip label={t("common.delete")}>
          <button
            type="button"
            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
            onClick={() => onDelete(invoice)}
          >
            <MdDeleteForever style={{ fontSize: "18px" }} />
          </button>
        </Tooltip>
      </div>
    </li>
  );
};

const Invoices = () => {
  const { t, locale } = useI18n();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { currentOrgId, members } = useSelector((state) => state.organizations);
  const { clients } = useSelector((state) => state.clients);
  const { projects } = useSelector((state) => state.projects);
  const { invoices, isLoading } = useSelector((state) => state.invoices);

  const [range, setRangeState] = useState(() => defaultRange(t));
  const [clientId, setClientId] = useState("");
  const [projectIds, setProjectIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(null);
  const [showGenerateWithId, setShowGenerateWithId] = useState(false);

  const isAdminOrOwner = computeIsAdminOrOwner(user, members);
  // Tracks whether the user has touched the date range themselves, so the
  // most-recent-entry lookup below never clobbers a choice they already
  // made - a ref rather than state since it's read from inside an async
  // callback (see the effect below), where a state closure would go stale.
  const rangeTouchedRef = useRef(false);
  const setRange = (start, end) => {
    rangeTouchedRef.current = true;
    setRangeState({ start, end });
  };
  const sortedClients = [...clients].sort((a, b) => a.name.localeCompare(b.name));
  const clientProjects = projects
    .filter((p) => p.clientId === clientId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const visibleInvoices = statusFilter.length
    ? invoices.filter((i) => statusFilter.includes(i.status))
    : invoices;

  useEffect(() => {
    if (currentOrgId) {
      dispatch(listClientsApi(currentOrgId, user.token));
      dispatch(listProjectsApi(currentOrgId, user.token));
      dispatch(listMembersApi(currentOrgId, user.token));
    }
  }, [dispatch, currentOrgId, user.token]);

  // Defaults the date range's end to the user's most recently logged time
  // entry instead of today, so the preselected month reflects their last
  // activity rather than the real current date. Only applies once, and only
  // if the range is still untouched by the time it resolves.
  useEffect(() => {
    if (!currentOrgId) return;
    let cancelled = false;
    dispatch(fetchLatestEntryDayApi(currentOrgId, user.token)).then((day) => {
      if (cancelled || !day || rangeTouchedRef.current) return;
      setRangeState(defaultRange(t, fromISODate(day)));
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId]);

  // All of the client's projects are included by default when switching
  // clients, matching "optional filter, everything selected until narrowed".
  useEffect(() => {
    setProjectIds(projects.filter((p) => p.clientId === clientId).map((p) => p.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projects]);

  const isValidRange = range.start <= range.end;

  const refreshInvoices = () => {
    if (!currentOrgId || !clientId || !isAdminOrOwner) return;
    if (!isValidRange) {
      toast.error(t("errors.invalidDateRange"), toastOptions);
      dispatch(clearInvoices());
      return;
    }
    dispatch(listInvoicesApi(currentOrgId, clientId, range.start, range.end, user.token));
  };

  useEffect(() => {
    refreshInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, clientId, range.start, range.end, isAdminOrOwner]);

  if (!currentOrgId) {
    return <NeedOrganizationEmptyState body={t("organizations.selectOrCreateFirst")} />;
  }

  if (!isAdminOrOwner) {
    return <p className="cw-error">{t("invoices.noAccess")}</p>;
  }

  const runAction = async (apiThunk, ...extraArgs) => {
    setError("");
    if (!clientId) {
      setError(t("invoices.clientRequired"));
      return;
    }
    if (!isValidRange) {
      toast.error(t("errors.invalidDateRange"), toastOptions);
      return;
    }
    setBusy(true);
    try {
      await dispatch(apiThunk(currentOrgId, clientId, range.start, range.end, projectIds, user.token, ...extraArgs));
      refreshInvoices();
    } catch (err) {
      setError(apiErrorMessage(err, locale));
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateWithId = async (number) => {
    setShowGenerateWithId(false);
    await runAction(generateInvoiceApi, number);
  };

  const handleDelete = async () => {
    const invoiceId = deletingInvoice.id;
    setDeletingInvoice(null);
    await dispatch(deleteInvoiceApi(currentOrgId, invoiceId, user.token));
  };

  return (
    <div className={styles.main}>
      <h1 className="cw-title">
        <FaFileInvoiceDollar className={styles.titleIcon} /> {t("nav.invoices")}
      </h1>

      <div className={styles.toolbar}>
        <AutocompleteSelect
          label={t("common.client")}
          placeholder={t("invoices.selectAClient")}
          options={sortedClients.map((c) => ({ value: c.id, label: c.name }))}
          value={clientId}
          onChange={setClientId}
        />

        <MultiSelect
          label={t("projects.title")}
          options={clientProjects.map((p) => ({ value: p.id, label: p.name }))}
          selected={projectIds}
          onChange={setProjectIds}
        />

        <MultiSelect
          label={t("invoices.status")}
          options={STATUSES.map((s) => ({ value: s, label: t(STATUS_LABEL_KEY[s]) }))}
          selected={statusFilter}
          onChange={setStatusFilter}
        />

        <DateRangePicker start={range.start} end={range.end} onChange={setRange} shortcutKeys={INVOICE_SHORTCUT_KEYS} />
      </div>

      <div className={styles.actions}>
        <Button variant="secondary" disabled={busy} onClick={() => runAction(previewInvoiceApi)} title={t("invoices.previewHint")}>
          {t("invoices.preview")}
        </Button>
        <Button disabled={busy} onClick={() => runAction(generateInvoiceApi)} title={t("invoices.generateHint")}>
          {t("invoices.generate")}
        </Button>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={() => setShowGenerateWithId(true)}
          title={t("invoices.generateWithIdHint")}
        >
          {t("invoices.generateWithId")}
        </Button>
      </div>

      {error && <p className="cw-error">{error}</p>}

      <h2 className="cw-subtitle">{t("invoices.listTitle")}</h2>
      {isLoading && <Spinner />}
      {!isLoading && invoices.length === 0 && (
        <EmptyState title={t("invoices.emptyTitle")} body={t("invoices.emptyBody")} />
      )}
      {!isLoading && invoices.length > 0 && (
        <ul className="cw-list">
          {visibleInvoices.map((invoice) => (
            <InvoiceRow
              key={invoice.id}
              invoice={invoice}
              orgId={currentOrgId}
              token={user.token}
              onDelete={setDeletingInvoice}
            />
          ))}
        </ul>
      )}

      <ConfirmModal
        show={!!deletingInvoice}
        title={t("invoices.deleteInvoiceTitle")}
        body={deletingInvoice ? t("invoices.deleteInvoiceBody", { number: deletingInvoice.number }) : ""}
        confirmLabel={t("common.delete")}
        onConfirm={handleDelete}
        onCancel={() => setDeletingInvoice(null)}
      />

      <GenerateInvoiceIdModal
        show={showGenerateWithId}
        onClose={() => setShowGenerateWithId(false)}
        onConfirm={handleGenerateWithId}
      />
    </div>
  );
};

export default Invoices;
